import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Modal, Button, Typography, makeStyles,
} from '@material-ui/core';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';

import { api_call, console_log, get_utc_timestamp } from '../helpers/untils';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '480px',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 3,
  outline: 'none',
};

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: theme.spacing(1),
  },
  amount: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  loadingWrap: {
    padding: theme.spacing(3, 0),
    textAlign: 'center',
    color: '#6b7280',
  },
  errorBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    border: '1.5px solid #dc2626',
    background: '#fef2f2',
    color: '#dc2626',
    borderRadius: '8px',
    fontSize: '13px',
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  paymentElementWrap: {
    position: 'relative',
    '& a[href*="stripe.com"]': { display: 'none !important' },
    '& a[href*="link.com"]': { display: 'none !important' },
    '& [class*="PoweredByStripe"]': { display: 'none !important' },
    '& [class*="powered-by"]': { display: 'none !important' },
    '& [class*="LinkButton"]': { display: 'none !important' },
  },
  holderNameField: {
    marginBottom: '12px',
    fontFamily: '"DM Sans", system-ui, sans-serif',
    textAlign: 'left',
  },
  holderNameLabel: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: 500,
    fontSize: '13px',
    color: '#374151',
  },
  holderNameInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1.5px solid #e5e7eb',
    borderRadius: '10px',
    padding: '8px 14px',
    fontFamily: 'inherit',
    fontSize: '16px',
    color: '#111827',
    backgroundColor: '#ffffff',
    outline: 'none',
    boxShadow: 'none',
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:focus': {
      border: '1.5px solid #2563eb',
      boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
    },
    '&:disabled': {
      backgroundColor: '#f9fafb',
      color: '#9ca3af',
      cursor: 'not-allowed',
    },
  },
  holderNameInputError: {
    border: '1.5px solid #dc2626',
    '&:focus': {
      border: '1.5px solid #dc2626',
      boxShadow: '0 0 0 3px rgba(220,38,38,0.12)',
    },
  },
  holderNameErrorText: {
    marginTop: '6px',
    fontSize: '12px',
    color: '#dc2626',
  },
}));

function StripePaymentInnerForm(props) {
  const { amount, isProcessing, setIsProcessing, errorMessage, setErrorMessage, onSuccess, onClose } = props;
  const classes = useStyles();
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || isProcessing) return;

    setIsProcessing(true);
    setErrorMessage('');
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMessage(error.message || 'Payment failed');
      setIsProcessing(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      await onSuccess(paymentIntent);
      return;
    }
    setErrorMessage(`Unexpected payment status: ${paymentIntent?.status}`);
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={classes.paymentElementWrap}>
        <PaymentElement
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card'],
            wallets: {
              applePay: 'never',
              googlePay: 'never',
              link: "never",
            },
            fields: {
              billingDetails: {
                name: 'auto',
              },
            },
            terms: { card: 'never' },
          }}
        />
      </div>

      {errorMessage ? (
        <div className={classes.errorBox} role="alert">{errorMessage}</div>
      ) : null}

      <div className={classes.buttons}>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          disabled={!stripe || isProcessing}
        >
          {isProcessing ? 'Processing…' : `Pay $${Number(amount).toFixed(2)}`}
        </Button>
        <Button type="button" color="primary" variant="outlined" onClick={onClose} disabled={isProcessing}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const StripePaymentModal = (props) => {
  const {
    open, setOpen, item, setItem,
    amount = 10, itemType = 'device_credit', callback,
    user_type, user_id,
    updateTimestamp, setUpdateTimestamp,
  } = props;

  const classes = useStyles();

  const [stripePublishableKey, setStripePublishableKey] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingError, setLoadingError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const didInit = useRef(false);

  const initPaymentIntent = async () => {
    setLoadingError('');
    try {
      const response = await api_call('/api/payment/stripe/installer-payment-intent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          itemId: item?.id,
          itemType,
          user_type,
          user_id,
          paymentMethodTypes: ['card'],
        }),
      });
      if (!response.ok) {
        const errObj = response;
        setLoadingError(errObj?.error || errObj?.message || 'Failed to initialize payment');
        return;
      }
      const data = await response.json();
      setStripePublishableKey(data.STRIPE_PUBLISHABLE_KEY);
      setClientSecret(data.clientSecret);
    } catch (err) {
      setLoadingError(err.message || 'Failed to initialize payment');
    }
  };

  useEffect(() => {
    if (stripePublishableKey) {
      setStripePromise(loadStripe(stripePublishableKey));
    }
  }, [stripePublishableKey]);

  useEffect(() => {
    if (!open || didInit.current) return;
    didInit.current = true;
    initPaymentIntent();
  }, [open]);

  const resetState = () => {
    didInit.current = false;
    setClientSecret(null);
    setLoadingError('');
    setErrorMessage('');
    setIsProcessing(false);
  };

  const handleClose = () => {
    resetState();
    setOpen(false);
  };

  const onSuccess = async (paymentIntent) => {
    try {
      const response = await api_call('/api/payment/stripe/installer-payment-complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          itemType,
          paymentIntentId: paymentIntent.id,
          itemId: item?.id,
          userType: user_type,
          user_id,
        }),
      });
      if (response.ok) {
        const apiRes = await response.json();
        callback && callback('success', apiRes);
        setUpdateTimestamp && setUpdateTimestamp(get_utc_timestamp());
        resetState();
        setOpen(false);
      } else {
        callback && callback('failed', null);
        resetState();
        setOpen(false);
      }
    } catch (err) {
      console_log('installer payment complete error:', err);
      callback && callback('failed', null);
      resetState();
      setOpen(false);
    }
  };

  //////////////////////// app modal close feature //////////////////////////////////////////
  const appModalCloseTimestamp = useSelector((state) => state.layout.appModalCloseTimestamp);
  useEffect(() => {
    if (appModalCloseTimestamp) {
      setOpen(false);
    }
  }, [appModalCloseTimestamp]);
  ///////////////////////////////////////////////////////////////////////////////////////////

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="stripe-payment-title"
    >
      <Box sx={modalStyle}>
        <Typography id="stripe-payment-title" variant="h6" className={classes.title}>
          Pay with Card
        </Typography>
        <Typography className={classes.amount}>
          Amount: ${Number(amount).toFixed(2)}
        </Typography>

        {
          loadingError ? (
            <>
              <div className={classes.errorBox}>{loadingError}</div>
              <div className={classes.buttons}>
                <Button type="button" color="primary" variant="outlined" onClick={handleClose}>
                  Close
                </Button>
              </div>
            </>
          ) : (!clientSecret || !stripePromise) ? (
            <div className={classes.loadingWrap}>Preparing secure payment form…</div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: {
                    colorPrimary: '#2563eb',
                    colorBackground: '#ffffff',
                    colorText: '#111827',
                    colorDanger: '#dc2626',
                    fontFamily: '"DM Sans", system-ui, sans-serif',
                    borderRadius: '10px',
                    spacingUnit: '4px',
                  },
                  rules: {
                    '.Input': {
                      border: '1.5px solid #e5e7eb',
                      boxShadow: 'none',
                      padding: '10px 14px',
                    },
                    '.Input:focus': {
                      border: '1.5px solid #2563eb',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
                    },
                    '.Label': {
                      fontWeight: '500',
                      fontSize: '13px',
                      color: '#374151',
                    },
                  },
                },
              }}
            >
              <StripePaymentInnerForm
                amount={amount}
                isProcessing={isProcessing}
                setIsProcessing={setIsProcessing}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                onSuccess={onSuccess}
                onClose={handleClose}
              />
            </Elements>
          )
        }
      </Box>
    </Modal>
  );
};

export default StripePaymentModal;
